/**
 * Answer Buttons Component
 *
 * Colorful answer buttons for multiple choice questions.
 */

interface AnswerOption {
  id: string;
  value: string;
  label: string;
  color: string;
}

interface AnswerButtonsProps {
  options: AnswerOption[];
  selectedAnswer?: unknown;
  correctAnswer?: unknown;
  showCorrect?: boolean;
  disabled?: boolean;
  onSelect: (value: string) => void;
}

const COLOR_CLASSES: Record<string, { bg: string; hover: string; selected: string; text: string }> = {
  red: {
    bg: 'bg-red-500',
    hover: 'hover:bg-red-400',
    selected: 'ring-4 ring-white',
    text: 'text-white',
  },
  blue: {
    bg: 'bg-blue-500',
    hover: 'hover:bg-blue-400',
    selected: 'ring-4 ring-white',
    text: 'text-white',
  },
  yellow: {
    bg: 'bg-yellow-500',
    hover: 'hover:bg-yellow-400',
    selected: 'ring-4 ring-white',
    text: 'text-black',
  },
  green: {
    bg: 'bg-green-500',
    hover: 'hover:bg-green-400',
    selected: 'ring-4 ring-white',
    text: 'text-white',
  },
  purple: {
    bg: 'bg-purple-500',
    hover: 'hover:bg-purple-400',
    selected: 'ring-4 ring-white',
    text: 'text-white',
  },
  orange: {
    bg: 'bg-orange-500',
    hover: 'hover:bg-orange-400',
    selected: 'ring-4 ring-white',
    text: 'text-white',
  },
};

const SHAPES = ['triangle', 'diamond', 'circle', 'square', 'pentagon', 'hexagon'];

const ShapeIcon = ({ shape, className }: { shape: string; className?: string }) => {
  switch (shape) {
    case 'triangle':
      return (
        <svg className={className} viewBox="0 0 24 24" fill="currentColor">
          <path d="M12 2L2 22h20L12 2z" />
        </svg>
      );
    case 'diamond':
      return (
        <svg className={className} viewBox="0 0 24 24" fill="currentColor">
          <path d="M12 2L2 12l10 10 10-10L12 2z" />
        </svg>
      );
    case 'circle':
      return (
        <svg className={className} viewBox="0 0 24 24" fill="currentColor">
          <circle cx="12" cy="12" r="10" />
        </svg>
      );
    case 'square':
      return (
        <svg className={className} viewBox="0 0 24 24" fill="currentColor">
          <rect x="3" y="3" width="18" height="18" />
        </svg>
      );
    case 'pentagon':
      return (
        <svg className={className} viewBox="0 0 24 24" fill="currentColor">
          <path d="M12 2L2 9l4 13h12l4-13L12 2z" />
        </svg>
      );
    case 'hexagon':
      return (
        <svg className={className} viewBox="0 0 24 24" fill="currentColor">
          <path d="M12 2L4 7v10l8 5 8-5V7l-8-5z" />
        </svg>
      );
    default:
      return null;
  }
};

export function AnswerButtons({
  options,
  selectedAnswer,
  correctAnswer,
  showCorrect = false,
  disabled = false,
  onSelect,
}: AnswerButtonsProps) {
  // Helper to check if an answer matches this option (could be id or value)
  const matchesOption = (answer: unknown, option: AnswerOption) =>
    answer === option.id || answer === option.value;

  const getButtonClasses = (option: AnswerOption, _index: number) => {
    const colorStyle = COLOR_CLASSES[option.color] || COLOR_CLASSES.blue;
    const isSelected = matchesOption(selectedAnswer, option);
    const isCorrect = showCorrect && matchesOption(correctAnswer, option);
    const isWrong = showCorrect && isSelected && !matchesOption(correctAnswer, option);

    let classes = `relative w-full p-6 rounded-xl font-bold text-lg transition-all duration-200 ${colorStyle.text}`;

    if (isCorrect) {
      classes += ` bg-green-500 ring-4 ring-green-300`;
    } else if (isWrong) {
      classes += ` bg-red-500 ring-4 ring-red-300 opacity-60`;
    } else if (isSelected) {
      classes += ` ${colorStyle.bg} ${colorStyle.selected}`;
    } else if (disabled) {
      classes += ` ${colorStyle.bg} opacity-40 cursor-not-allowed`;
    } else {
      classes += ` ${colorStyle.bg} ${colorStyle.hover} cursor-pointer transform hover:scale-102 active:scale-98`;
    }

    return classes;
  };

  // Determine grid layout based on number of options
  const gridClasses = options.length <= 2
    ? 'grid-cols-1 md:grid-cols-2'
    : options.length <= 4
    ? 'grid-cols-2'
    : 'grid-cols-2 md:grid-cols-3';

  return (
    <div className={`grid ${gridClasses} gap-4`}>
      {options.map((option, index) => (
        <button
          key={option.id}
          type="button"
          disabled={disabled || selectedAnswer !== undefined}
          onClick={() => onSelect(option.value)}
          className={getButtonClasses(option, index)}
        >
          <div className="flex items-center justify-center gap-3">
            <ShapeIcon shape={SHAPES[index % SHAPES.length]} className="w-6 h-6 opacity-30" />
            <span className="truncate">{option.label}</span>
          </div>

          {/* Selection indicator */}
          {matchesOption(selectedAnswer, option) && (
            <div className="absolute top-2 right-2 w-6 h-6 bg-white rounded-full flex items-center justify-center">
              <svg className="w-4 h-4 text-gray-900" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
              </svg>
            </div>
          )}

          {/* Correct indicator */}
          {showCorrect && matchesOption(correctAnswer, option) && (
            <div className="absolute top-2 right-2 w-8 h-8 bg-green-300 rounded-full flex items-center justify-center animate-bounce">
              <svg className="w-5 h-5 text-green-800" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
              </svg>
            </div>
          )}
        </button>
      ))}
    </div>
  );
}

export default AnswerButtons;
